import moment from 'moment'
import { randomNumber } from './numbers'

// max value of seconds to be subtracted from the date to make it more random
const RANDOM_SECONDS = 20
const SECONDS_IN_HOUR = 3600
export const SECONDS_IN_DAY = 24 * SECONDS_IN_HOUR
export const MAX_DAYS_MONTH_CAN_HAVE = 31

export const startOfDay = (options = {}, type = 'timestamp') => {
  const { date, timezone } = options

  let dateObj = new Date()

  if (type === 'timestamp' && date === Number(date)) {
    dateObj = new Date(1000 * date)
  } else if (date) {
    dateObj = date
  }

  const parts = Intl.DateTimeFormat('en-US', {
    timezone,
    hourCycle: 'h23',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(dateObj)
  const hour = parseInt(parts.find((i) => i.type === 'hour').value)
  const minute = parseInt(parts.find((i) => i.type === 'minute').value)
  const second = parseInt(parts.find((i) => i.type === 'second').value)
  const dateTimestamp = Math.floor(
    (dateObj - hour * SECONDS_IN_HOUR * 1000 - minute * 60000 - second * 1000)
      / 1000,
  )

  return type === 'timestamp' ? dateTimestamp : new Date(1000 * dateTimestamp)
}

export const endOfDay = (...args) => {
  const type = args[1]
  const timestamp = startOfDay(...args) + SECONDS_IN_DAY - 1
  return type === 'timestamp' ? timestamp : new Date(1000 * timestamp)
}

export const dateRangeToArray = (fromDate, toDate) => {
  // To randomize startTime an arbitrary value of 0..RANDOM_SECONDS seconds is being subtracted
  // when it's set to start date in randomizedStartOfDay()
  // To account for that, we can always add RANDOM_SECONDS seconds to fromDate

  // Ensure dates are moment objects
  const updatedFromDate = moment(
    fromDate && fromDate.add(RANDOM_SECONDS, 'seconds'),
  ).startOf('day')
  const updatedToDate = moment(toDate).startOf('day')

  const dates = [updatedFromDate]

  while (dates[dates.length - 1].isBefore(updatedToDate) && dates.length < 10) {
    dates.push(moment(dates[dates.length - 1]).add(1, 'd'))
  }

  return dates
}

export const momentFromUnix = (timestamp) => moment(timestamp * 1000)
export const nowToUnix = () => Math.round(Date.now() / 1000)

// get min startTime in a series
export const getMinStartTime = (series) => {
  if (!series || !series.length) return null
  return series.reduce((item, acc) =>
    (item.startTime < acc.startTime ? item : acc)).startTime
}

// get max endTime in a series
export const getMaxEndTime = (series) => {
  if (!series || !series.length) return null
  return series.reduce((item, acc) => (item.endTime > acc.endTime ? item : acc))
    .endTime
}

export const meetingStartToEnd = (
  startTime,
  endTime,
  userTimezone = moment.tz.guess(),
) => {
  const start = momentFromUnix(startTime).tz(userTimezone)
  const end = momentFromUnix(endTime).tz(userTimezone)
  return `${start.format('ddd, MMM D · h:mma')} - ${end.format('h:mma')}`
}

export const meetingFormattedDate = (
  timestamp,
  userTimezone = moment.tz.guess(),
  format = 'ddd, MMM D'
) => {
  const date = momentFromUnix(timestamp).tz(userTimezone)
  return `${date.format(format)}`
}

/**
 * Adjusts the Moment object to a given timezone
 * @param {*} momentObj     a Moment object (e.g. moment('Feb 14, 2021 12:00 pm'))
 * @param {string} timezone desired timezone (e.g. 'UTC')
 */
export const setTimezone = (momentObj, timezone) => {
  const [year, month, date, hours, minutes, seconds, milliseconds] = momentObj.toArray()
  const result = momentObj.tz(timezone || moment.tz.guess())

  result
    .year(year)
    .month(month)
    .date(date)
    .hours(hours)
    .minutes(minutes)
    .seconds(seconds)
    .milliseconds(milliseconds)

  return result
}

// get moment() object for a given user
export const getMoment = (user) => {
  if (!user) {
    return moment()
  }
  return moment.tz(new Date(), user.timezone || moment.tz.guess())
}

const getParts = (dateObject, timezone) => {
  const parts = Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(dateObject)
  const year = parseInt(parts.find((i) => i.type === 'year').value)
  const month = parseInt(parts.find((i) => i.type === 'month').value)
  const day = parseInt(parts.find((i) => i.type === 'day').value)

  return { year, month, day }
}

export const isSameDay = (dateObjectA, dateObjectB, timezone) => {
  const {
    year: yearToday,
    month: monthToday,
    day: dayToday,
  } = getParts(dateObjectA, timezone)

  const {
    year: yearEvent,
    month: monthEvent,
    day: dayEvent,
  } = getParts(dateObjectB, timezone)

  return dayToday === dayEvent
    && monthToday === monthEvent
    && yearToday === yearEvent
}

/**
 * Returns true if the meeting is to happen today
 * @param {Meeting} event a meeting object
 */
export const isToday = (unixtime, timezone) =>
  isSameDay(new Date(), new Date(unixtime * 1000), timezone)

export const randomizedStartOfDay = (user) => {
  const timezone = user?.timezone || moment.tz.guess()
  return setTimezone(moment(), timezone)
    .startOf('day')
    .subtract(randomNumber(0, RANDOM_SECONDS), 'seconds')
}

export const momentTimezoneGuess = () => moment.tz.guess()
